import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    ArrowRight, 
    ArrowLeft, 
    Check,
    Mail,
    Phone,
    User,
    Lock,
    Eye,
    EyeOff,
    AtSign,
    MapPin,
    Briefcase,
    GraduationCap,
    Heart,
    Users,
    Building,
    HelpCircle,
    Target
} from 'lucide-react';

// New logo image URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png";

const EDUCATION_OPTIONS = [
    'High School',
    'Some College',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctorate',
    'Other'
];

const MARITAL_STATUS_OPTIONS = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
    { value: 'separated', label: 'Separated' }
];

const ENTREPRENEUR_STATUS_OPTIONS = [
    { value: 'yes', label: 'Yes, I am an entrepreneur' },
    { value: 'aspiring', label: 'Aspiring entrepreneur' },
    { value: 'no', label: 'No' }
];

const EMPLOYMENT_STATUS_OPTIONS = [
    { value: 'employed', label: 'Employed' },
    { value: 'self-employed', label: 'Self-Employed' },
    { value: 'unemployed', label: 'Unemployed' },
    { value: 'retired', label: 'Retired' },
    { value: 'student', label: 'Student' }
];

const INCOME_RANGE_OPTIONS = [
    { value: 'under_25k', label: 'Under $25,000' },
    { value: '25k_50k', label: '$25,000 - $50,000' },
    { value: '50k_75k', label: '$50,000 - $75,000' },
    { value: '75k_100k', label: '$75,000 - $100,000' },
    { value: '100k_150k', label: '$100,000 - $150,000' },
    { value: '150k_200k', label: '$150,000 - $200,000' },
    { value: 'over_200k', label: 'Over $200,000' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

const COMMITTEE_OPTIONS = [
    'Mentorship (Brother 2 Brother)',
    'Fundraising',
    'Community Engagement',
    'Professional Development',
    'Recruitment',
    'IT/Marketing'
];

const REFERRAL_SOURCE_OPTIONS = [
    'Social Media (Instagram, Facebook, etc.)',
    'Friend or Family Member',
    'Recovery Room Event',
    'Eventbrite',
    'Website Search',
    'Community Organization',
    'Professional Referral',
    'LinkedIn',
    'Podcast/Media',
    'Church/Faith Community',
    'Other'
];

const INTEREST_OPTIONS = [
    'Mental health & wellness',
    'Professional networking',
    'Mentorship',
    'Community service',
    'Personal growth',
    'Spiritual development',
    'Financial wellness',
    'Physical fitness',
    'Creative expression',
    'Leadership development'
];

const MemberRegisterPage = () => {
    const navigate = useNavigate();
    const { registerMember } = useAuth();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [hobbyInput, setHobbyInput] = useState('');
    const [formData, setFormData] = useState({
        // Step 1: Account credentials
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        // Step 2: Basic info
        name: '',
        phone: '',
        location: '',
        birth_city: '',
        // Step 3: Professional
        profession: '',
        job_title: '',
        employer: '',
        employment_status: '',
        entrepreneur_status: '',
        income_range: '',
        // Step 4: Personal
        education: '',
        marital_status: '',
        has_children: false,
        num_children: 0,
        has_siblings: false,
        num_siblings: 0,
        // Step 5: Interests
        hobbies: [],
        interests: [],
        committee_interest: [],
        referral_source: '',
        referral_source_other: '',
        // Step 6: Needs & Resources
        current_needs: '',
        challenges: '',
        support_growth: '',
        immediate_help: '',
    });

    const totalSteps = 6;
    const progress = (currentStep / totalSteps) * 100;

    const stepTitles = [
        'Account Setup',
        'Personal Info',
        'Professional',
        'Background',
        'Interests',
        'Needs & Resources'
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleArrayItem = (field, item) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(item)
                ? prev[field].filter(i => i !== item)
                : [...prev[field], item]
        }));
    };

    const addHobby = () => {
        if (hobbyInput.trim() && !formData.hobbies.includes(hobbyInput.trim())) {
            setFormData(prev => ({
                ...prev,
                hobbies: [...prev.hobbies, hobbyInput.trim()]
            }));
            setHobbyInput('');
        }
    };

    const removeHobby = (hobby) => {
        setFormData(prev => ({
            ...prev,
            hobbies: prev.hobbies.filter(h => h !== hobby)
        }));
    };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!formData.username.trim()) {
                    toast.error('Please choose a username');
                    return false;
                }
                if (formData.username.length < 3) {
                    toast.error('Username must be at least 3 characters');
                    return false;
                }
                if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
                    toast.error('Username can only contain letters, numbers, and underscores');
                    return false;
                }
                if (!formData.email.trim()) {
                    toast.error('Please enter your email');
                    return false;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                    toast.error('Please enter a valid email address');
                    return false;
                }
                if (!formData.password) {
                    toast.error('Please enter a password');
                    return false;
                }
                if (formData.password.length < 8) {
                    toast.error('Password must be at least 8 characters');
                    return false;
                }
                if (formData.password !== formData.confirmPassword) {
                    toast.error('Passwords do not match');
                    return false;
                }
                return true;
            case 2:
                if (!formData.name.trim()) {
                    toast.error('Please enter your full name');
                    return false;
                }
                if (!formData.phone.trim()) {
                    toast.error('Please enter your phone number');
                    return false;
                }
                if (!formData.location.trim()) {
                    toast.error('Please enter your current location');
                    return false;
                }
                if (!formData.birth_city.trim()) {
                    toast.error('Please enter your birth city');
                    return false;
                }
                return true;
            case 3:
                if (!formData.profession.trim()) {
                    toast.error('Please enter your profession');
                    return false;
                }
                if (!formData.employment_status) {
                    toast.error('Please select your employment status');
                    return false;
                }
                if (!formData.entrepreneur_status) {
                    toast.error('Please select your entrepreneur status');
                    return false;
                }
                if (!formData.income_range) {
                    toast.error('Please select your income range');
                    return false;
                }
                return true;
            case 4:
                if (!formData.education) {
                    toast.error('Please select your education level');
                    return false;
                }
                if (!formData.marital_status) {
                    toast.error('Please select your marital status');
                    return false;
                }
                return true;
            case 5:
                if (formData.interests.length === 0) {
                    toast.error('Please select at least one area of interest');
                    return false;
                }
                if (!formData.referral_source) {
                    toast.error('Please tell us how you learned about Recovery Room');
                    return false;
                }
                if (formData.referral_source === 'Other' && !formData.referral_source_other.trim()) {
                    toast.error('Please specify how you heard about us');
                    return false;
                }
                return true;
            case 6:
                // Step 6 fields are optional but encouraged
                return true;
            default:
                return true;
        }
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, totalSteps));
            window.scrollTo(0, 0);
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        window.scrollTo(0, 0);
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) return;
        
        setIsSubmitting(true);
        try {
            await registerMember({
                username: formData.username,
                email: formData.email,
                password: formData.password,
                name: formData.name,
                phone: formData.phone,
                location: formData.location,
                birth_city: formData.birth_city,
                profession: formData.profession,
                job_title: formData.job_title || null,
                employer: formData.employer || null,
                employment_status: formData.employment_status,
                entrepreneur_status: formData.entrepreneur_status,
                income_range: formData.income_range,
                education: formData.education,
                marital_status: formData.marital_status,
                has_children: formData.has_children,
                num_children: formData.has_children ? formData.num_children : 0,
                has_siblings: formData.has_siblings,
                num_siblings: formData.has_siblings ? formData.num_siblings : 0,
                hobbies: formData.hobbies,
                interests: formData.interests,
                committee_interest: formData.committee_interest,
                referral_source: formData.referral_source === 'Other' ? formData.referral_source_other : formData.referral_source,
                current_needs: formData.current_needs || null,
                challenges: formData.challenges || null,
                support_growth: formData.support_growth || null,
                immediate_help: formData.immediate_help || null,
            });
            toast.success('Account created successfully! Welcome to Recovery Room.');
            navigate('/member/dashboard');
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail === 'Username already taken') {
                toast.error('This username is already taken. Please choose another.');
                setCurrentStep(1);
            } else if (detail === 'Email already registered') {
                toast.error('This email is already registered. Please login instead.');
                setCurrentStep(1);
            } else {
                toast.error(detail || 'Registration failed. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#3D4435] flex flex-col">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#3D4435]/90 backdrop-blur-lg border-b border-[#5A6350]">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/join" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Recovery Room Logo" className="w-10 h-10 object-contain" />
                        <div>
                            <h1 className="text-xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/member/login" 
                            className="text-sm text-[#C9A227] hover:text-[#E8D48A] font-medium"
                            data-testid="login-link"
                        >
                            Already a member? Sign In
                        </Link>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-6 pt-28 pb-12">
                <div className="w-full max-w-2xl">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-white font-['Playfair_Display'] mb-2">
                            Join the Brotherhood
                        </h2>
                        <p className="text-[#A0A0A0]">
                            Complete your profile to become a member of Recovery Room.
                        </p>
                    </div>

                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl overflow-hidden">
                        {/* Progress Header */}
                        <div className="p-6 border-b border-[#5A6350]">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-white">
                                    {stepTitles[currentStep - 1]}
                                </h3>
                                <span className="text-sm text-[#A0A0A0]">
                                    Step {currentStep} of {totalSteps}
                                </span>
                            </div>
                            <Progress value={progress} className="h-1 bg-[#5A6350]" />
                            <div className="flex justify-between mt-3">
                                {stepTitles.map((title, idx) => (
                                    <span 
                                        key={idx}
                                        className={`text-xs font-medium ${currentStep > idx ? 'text-[#C9A227]' : currentStep === idx + 1 ? 'text-white' : 'text-[#666]'}`}
                                    >
                                        {idx + 1}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <CardContent className="p-6">
                            {/* Step 1: Account Setup */}
                            {currentStep === 1 && (
                                <div className="space-y-5 animate-fade-in" data-testid="register-step-1">
                                    <div className="space-y-2">
                                        <Label htmlFor="username" className="text-white flex items-center gap-2">
                                            <AtSign className="w-4 h-4 text-[#C9A227]" />
                                            Username *
                                        </Label>
                                        <Input
                                            id="username"
                                            value={formData.username}
                                            onChange={(e) => handleInputChange('username', e.target.value.toLowerCase())}
                                            placeholder="Choose a username"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-username-input"
                                        />
                                        <p className="text-xs text-[#666]">Letters, numbers, and underscores only</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-white flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-[#C9A227]" />
                                            Email Address *
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            placeholder="your@email.com"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password" className="text-white flex items-center gap-2">
                                            <Lock className="w-4 h-4 text-[#C9A227]" />
                                            Password *
                                        </Label>
                                        <div className="relative">
                                            <Input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={(e) => handleInputChange('password', e.target.value)}
                                                placeholder="Create a password"
                                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] pr-10"
                                                data-testid="register-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-[#666]">At least 8 characters</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword" className="text-white flex items-center gap-2">
                                            <Lock className="w-4 h-4 text-[#C9A227]" />
                                            Confirm Password *
                                        </Label>
                                        <div className="relative">
                                            <Input
                                                id="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={formData.confirmPassword}
                                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                                placeholder="Confirm your password"
                                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] pr-10"
                                                data-testid="register-confirm-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Personal Info */}
                            {currentStep === 2 && (
                                <div className="space-y-5 animate-fade-in" data-testid="register-step-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="name" className="text-white flex items-center gap-2">
                                            <User className="w-4 h-4 text-[#C9A227]" />
                                            Full Name *
                                        </Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            placeholder="Enter your full name"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="text-white flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-[#C9A227]" />
                                            Phone Number *
                                        </Label>
                                        <Input
                                            id="phone"
                                            value={formData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            placeholder="(555) 123-4567"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-phone-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="location" className="text-white flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-[#C9A227]" />
                                            Current Location *
                                        </Label>
                                        <Input
                                            id="location"
                                            value={formData.location}
                                            onChange={(e) => handleInputChange('location', e.target.value)}
                                            placeholder="City, State (e.g., Washington, DC)"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-location-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="birth_city" className="text-white flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-[#C9A227]" />
                                            Birth City *
                                        </Label>
                                        <Input
                                            id="birth_city"
                                            value={formData.birth_city}
                                            onChange={(e) => handleInputChange('birth_city', e.target.value)}
                                            placeholder="Where were you born?"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-birth-city-input"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Professional */}
                            {currentStep === 3 && (
                                <div className="space-y-5 animate-fade-in" data-testid="register-step-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="profession" className="text-white flex items-center gap-2">
                                            <Briefcase className="w-4 h-4 text-[#C9A227]" />
                                            Profession/Industry *
                                        </Label>
                                        <Input
                                            id="profession"
                                            value={formData.profession}
                                            onChange={(e) => handleInputChange('profession', e.target.value)}
                                            placeholder="e.g., Healthcare, Technology, Education"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-profession-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="job_title" className="text-white flex items-center gap-2">
                                            <Briefcase className="w-4 h-4 text-[#C9A227]" />
                                            Job Title (Optional)
                                        </Label>
                                        <Input
                                            id="job_title"
                                            value={formData.job_title}
                                            onChange={(e) => handleInputChange('job_title', e.target.value)}
                                            placeholder="e.g., Software Engineer, Project Manager"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-job-title-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="employer" className="text-white flex items-center gap-2">
                                            <Building className="w-4 h-4 text-[#C9A227]" />
                                            Employer (Optional)
                                        </Label>
                                        <Input
                                            id="employer"
                                            value={formData.employer}
                                            onChange={(e) => handleInputChange('employer', e.target.value)}
                                            placeholder="Current employer"
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                            data-testid="register-employer-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Employment Status *</Label>
                                        <div className="flex flex-wrap gap-2">
                                            {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => handleInputChange('employment_status', option.value)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.employment_status === option.value
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Entrepreneur Status *</Label>
                                        <div className="flex flex-wrap gap-2">
                                            {ENTREPRENEUR_STATUS_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => handleInputChange('entrepreneur_status', option.value)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.entrepreneur_status === option.value
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Annual Income Range *</Label>
                                        <select
                                            value={formData.income_range}
                                            onChange={(e) => handleInputChange('income_range', e.target.value)}
                                            className="w-full h-10 px-3 rounded-md bg-[#3D4435] border border-[#5A6350] text-white focus:ring-[#C9A227]/30 focus:border-[#C9A227] outline-none"
                                            data-testid="register-income-range-select"
                                        >
                                            <option value="" className="bg-[#3D4435]">Select income range</option>
                                            {INCOME_RANGE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value} className="bg-[#3D4435]">
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Background */}
                            {currentStep === 4 && (
                                <div className="space-y-5 animate-fade-in" data-testid="register-step-4">
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <GraduationCap className="w-4 h-4 text-[#C9A227]" />
                                            Education Level *
                                        </Label>
                                        <div className="flex flex-wrap gap-2">
                                            {EDUCATION_OPTIONS.map((option) => (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    onClick={() => handleInputChange('education', option)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.education === option
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <Heart className="w-4 h-4 text-[#C9A227]" />
                                            Marital Status *
                                        </Label>
                                        <div className="flex flex-wrap gap-2">
                                            {MARITAL_STATUS_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => handleInputChange('marital_status', option.value)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.marital_status === option.value
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    id="has_children"
                                                    checked={formData.has_children}
                                                    onChange={(e) => handleInputChange('has_children', e.target.checked)}
                                                    className="w-4 h-4 rounded border-[#5A6350] bg-[#3D4435] text-[#C9A227] focus:ring-[#C9A227]"
                                                />
                                                <Label htmlFor="has_children" className="text-white cursor-pointer">I have children</Label>
                                            </div>
                                            {formData.has_children && (
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={formData.num_children}
                                                    onChange={(e) => handleInputChange('num_children', parseInt(e.target.value) || 0)}
                                                    placeholder="Number of children"
                                                    className="bg-[#3D4435] border-[#5A6350] text-white w-full"
                                                />
                                            )}
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    id="has_siblings"
                                                    checked={formData.has_siblings}
                                                    onChange={(e) => handleInputChange('has_siblings', e.target.checked)}
                                                    className="w-4 h-4 rounded border-[#5A6350] bg-[#3D4435] text-[#C9A227] focus:ring-[#C9A227]"
                                                />
                                                <Label htmlFor="has_siblings" className="text-white cursor-pointer">I have siblings</Label>
                                            </div>
                                            {formData.has_siblings && (
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={formData.num_siblings}
                                                    onChange={(e) => handleInputChange('num_siblings', parseInt(e.target.value) || 0)}
                                                    placeholder="Number of siblings"
                                                    className="bg-[#3D4435] border-[#5A6350] text-white w-full"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 5: Interests */}
                            {currentStep === 5 && (
                                <div className="space-y-5 animate-fade-in" data-testid="register-step-5">
                                    <div className="space-y-2">
                                        <Label className="text-white">Add Your Hobbies</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                value={hobbyInput}
                                                onChange={(e) => setHobbyInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHobby())}
                                                placeholder="Enter a hobby"
                                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                            />
                                            <Button
                                                type="button"
                                                onClick={addHobby}
                                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                                            >
                                                Add
                                            </Button>
                                        </div>
                                        {formData.hobbies.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {formData.hobbies.map((hobby, idx) => (
                                                    <span 
                                                        key={idx}
                                                        className="px-3 py-1 bg-[#C9A227]/20 text-[#C9A227] rounded-full text-sm flex items-center gap-2"
                                                    >
                                                        {hobby}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeHobby(hobby)}
                                                            className="hover:text-red-400"
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Areas of Interest * (Select at least one)</Label>
                                        <div className="flex flex-wrap gap-2">
                                            {INTEREST_OPTIONS.map((interest) => (
                                                <button
                                                    key={interest}
                                                    type="button"
                                                    onClick={() => toggleArrayItem('interests', interest)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.interests.includes(interest)
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {interest}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <Users className="w-4 h-4 text-[#C9A227]" />
                                            Committee Interest (Optional)
                                        </Label>
                                        <p className="text-[#666] text-sm">Select committees you'd be interested in joining</p>
                                        <div className="flex flex-wrap gap-2">
                                            {COMMITTEE_OPTIONS.map((committee) => (
                                                <button
                                                    key={committee}
                                                    type="button"
                                                    onClick={() => toggleArrayItem('committee_interest', committee)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.committee_interest.includes(committee)
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {committee}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Referral Source Question */}
                                    <div className="space-y-2 pt-4 border-t border-[#5A6350]">
                                        <Label className="text-white flex items-center gap-2">
                                            <Heart className="w-4 h-4 text-[#C9A227]" />
                                            How did you learn about the Recovery Room? *
                                        </Label>
                                        <div className="flex flex-wrap gap-2">
                                            {REFERRAL_SOURCE_OPTIONS.map((source) => (
                                                <button
                                                    key={source}
                                                    type="button"
                                                    onClick={() => handleInputChange('referral_source', source)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                        formData.referral_source === source
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                    data-testid={`referral-${source.toLowerCase().replace(/\s/g, '-')}`}
                                                >
                                                    {source}
                                                </button>
                                            ))}
                                        </div>
                                        {formData.referral_source === 'Other' && (
                                            <Input
                                                value={formData.referral_source_other}
                                                onChange={(e) => handleInputChange('referral_source_other', e.target.value)}
                                                placeholder="Please specify how you heard about us"
                                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] mt-2"
                                                data-testid="referral-other-input"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 6: Needs & Resources */}
                            {currentStep === 6 && (
                                <div className="space-y-5 animate-fade-in" data-testid="register-step-6">
                                    <div className="bg-[#3D4435] rounded-lg p-4 mb-4">
                                        <p className="text-[#A0A0A0] text-sm">
                                            Help us understand how we can best support you. These responses are optional but help us tailor resources and support to your needs.
                                        </p>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <HelpCircle className="w-4 h-4 text-[#C9A227]" />
                                            What current needs or resources are you looking for?
                                        </Label>
                                        <Textarea
                                            value={formData.current_needs}
                                            onChange={(e) => handleInputChange('current_needs', e.target.value)}
                                            placeholder="e.g., Career guidance, mentorship, networking opportunities, mental health support..."
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] min-h-[100px]"
                                            data-testid="register-current-needs"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <Target className="w-4 h-4 text-[#C9A227]" />
                                            What challenges or difficulties are you currently experiencing?
                                        </Label>
                                        <Textarea
                                            value={formData.challenges}
                                            onChange={(e) => handleInputChange('challenges', e.target.value)}
                                            placeholder="Share any challenges you're facing that we might be able to help address..."
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] min-h-[100px]"
                                            data-testid="register-challenges"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <Heart className="w-4 h-4 text-[#C9A227]" />
                                            How can we best support your growth and development?
                                        </Label>
                                        <Textarea
                                            value={formData.support_growth}
                                            onChange={(e) => handleInputChange('support_growth', e.target.value)}
                                            placeholder="What type of support would be most valuable to you..."
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] min-h-[100px]"
                                            data-testid="register-support-growth"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-white flex items-center gap-2">
                                            <Users className="w-4 h-4 text-[#C9A227]" />
                                            What immediate areas can we help facilitate in your development?
                                        </Label>
                                        <Textarea
                                            value={formData.immediate_help}
                                            onChange={(e) => handleInputChange('immediate_help', e.target.value)}
                                            placeholder="Are there any urgent needs or immediate areas where you'd like assistance..."
                                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] min-h-[100px]"
                                            data-testid="register-immediate-help"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Navigation Buttons */}
                            <div className="flex justify-between mt-8 pt-6 border-t border-[#5A6350]">
                                {currentStep > 1 ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={prevStep}
                                        className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                                        data-testid="register-prev-btn"
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                ) : (
                                    <div />
                                )}
                                
                                {currentStep < totalSteps ? (
                                    <Button
                                        type="button"
                                        onClick={nextStep}
                                        className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                        data-testid="register-next-btn"
                                    >
                                        Continue
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                        className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                        data-testid="register-submit-btn"
                                    >
                                        {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
                                        <Check className="w-4 h-4 ml-2" />
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-[#666] text-sm mt-6">
                        By creating an account, you agree to our community guidelines.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MemberRegisterPage;
